import { useEffect, useMemo, useRef, useState } from "react";
import { Pencil } from "lucide-react";
import t from "@/lang/lang";
import { Offer } from "@/services/backend/qpm/offers/types/offer";
import {
  useQpmOfferPutOfferConfigSettingsSnapshotMutation,
  useQpmOfferReloadOfferConfigSnapshotMutation,
} from "@/services/backend/qpm/offers/api/offer-api";
import { Button } from "@/shared/components/ui/button";
import { RTKQueryErrorAlert } from "@/shared/components/errors/rtk-query-error-alert";
import {
  ServiceProgramEntryEditor,
  ServiceProgramEntryValue,
  ServiceProgramGroupValue,
  ServiceProgramPageBreakValue,
} from "@/shared/components/domain/qpm/service-program-entry-editor";
import { usePersistenceStateUpdateOnTrigger } from "@/shared/lib/persistence-state/persistence-state-info";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/shared/components/ui/tooltip";

function resolveInitialPageBreaks(
  groups: ServiceProgramGroupValue[],
  pageBreaks?: ServiceProgramPageBreakValue[] | null,
  initialized?: boolean,
): ServiceProgramPageBreakValue[] {
  if (initialized) {
    return pageBreaks ?? [];
  }
  if (
    pageBreaks !== undefined &&
    pageBreaks !== null &&
    pageBreaks.length > 0
  ) {
    return pageBreaks;
  }
  const orderedGroups = [...groups].sort((a, b) => a.order - b.order);
  if (orderedGroups.length < 2) {
    return [];
  }
  return [
    {
      id: crypto.randomUUID(),
      anchorKind: "group",
      anchorId: orderedGroups[1].id,
      order: 1,
    },
  ];
}

export function OfferServiceProgramEditor({ offer }: { offer: Offer }) {
  const initialPageBreaksInitialized =
    offer.offerConfigSnapshot.settings.serviceProgramPageBreaksInitialized ??
    false;
  const initialGroups =
    offer.offerConfigSnapshot.settings.serviceProgramGroups ?? [];
  const initialPageBreaks = resolveInitialPageBreaks(
    initialGroups,
    offer.offerConfigSnapshot.settings.serviceProgramPageBreaks,
    initialPageBreaksInitialized,
  );
  const [entries, setEntries] = useState<ServiceProgramEntryValue[]>(
    offer.offerConfigSnapshot.settings.serviceProgramEntries ?? [],
  );
  const [groups, setGroups] =
    useState<ServiceProgramGroupValue[]>(initialGroups);
  const [pageBreaks, setPageBreaks] =
    useState<ServiceProgramPageBreakValue[]>(initialPageBreaks);
  const [isEditMode, setIsEditMode] = useState(false);
  const isEditModeRef = useRef(isEditMode);

  useEffect(() => {
    isEditModeRef.current = isEditMode;
  }, [isEditMode]);

  useEffect(() => {
    if (isEditModeRef.current) {
      return;
    }
    const nextInitialized =
      offer.offerConfigSnapshot.settings.serviceProgramPageBreaksInitialized ??
      false;
    const nextGroups =
      offer.offerConfigSnapshot.settings.serviceProgramGroups ?? [];
    setEntries(offer.offerConfigSnapshot.settings.serviceProgramEntries ?? []);
    setGroups(nextGroups);
    setPageBreaks(
      resolveInitialPageBreaks(
        nextGroups,
        offer.offerConfigSnapshot.settings.serviceProgramPageBreaks,
        nextInitialized,
      ),
    );
  }, [
    offer.id,
    offer.offerConfigSnapshot.settings.serviceProgramEntries,
    offer.offerConfigSnapshot.settings.serviceProgramGroups,
    offer.offerConfigSnapshot.settings.serviceProgramPageBreaks,
    offer.offerConfigSnapshot.settings.serviceProgramPageBreaksInitialized,
  ]);

  useEffect(() => {
    setIsEditMode(false);
  }, [offer.id]);

  const [putSettings, putSettingsState] =
    useQpmOfferPutOfferConfigSettingsSnapshotMutation();
  const [reloadSettings, reloadSettingsState] =
    useQpmOfferReloadOfferConfigSnapshotMutation();

  const request = useMemo(
    () => ({
      offerId: offer.id,
      ceoName: offer.offerConfigSnapshot.settings.ceoName ?? "",
      ceoGender: offer.offerConfigSnapshot.settings.ceoGender ?? "",
      allowedDiscounts:
        offer.offerConfigSnapshot.settings.allowedDiscounts ?? [],
      allowedSurcharges:
        offer.offerConfigSnapshot.settings.allowedSurcharges ?? [],
      offerClaimText: offer.offerConfigSnapshot.settings.offerClaimText ?? "",
      serviceProgramGroups: groups,
      serviceProgramEntries: entries,
      serviceProgramPageBreaks: pageBreaks,
      serviceProgramPageBreaksInitialized: true,
    }),
    [
      entries,
      offer.id,
      offer.offerConfigSnapshot.settings.allowedDiscounts,
      offer.offerConfigSnapshot.settings.allowedSurcharges,
      offer.offerConfigSnapshot.settings.ceoGender,
      offer.offerConfigSnapshot.settings.ceoName,
      offer.offerConfigSnapshot.settings.offerClaimText,
      pageBreaks,
      groups,
    ],
  );

  const { onTrigger: triggerSave } = usePersistenceStateUpdateOnTrigger(
    request,
    putSettings,
    putSettingsState.isLoading,
    putSettingsState.error,
    putSettingsState.isSuccess,
    putSettingsState.reset,
    { toastError: true, toastSuccess: true },
  );

  useEffect(() => {
    if (putSettingsState.isSuccess) {
      setIsEditMode(false);
    }
  }, [putSettingsState.isSuccess]);

  const isLocked = offer.immutable || putSettingsState.isLoading;
  const resetLocalFromSnapshot = () => {
    const nextInitialized =
      offer.offerConfigSnapshot.settings.serviceProgramPageBreaksInitialized ??
      false;
    const nextGroups =
      offer.offerConfigSnapshot.settings.serviceProgramGroups ?? [];
    setEntries(offer.offerConfigSnapshot.settings.serviceProgramEntries ?? []);
    setGroups(nextGroups);
    setPageBreaks(
      resolveInitialPageBreaks(
        nextGroups,
        offer.offerConfigSnapshot.settings.serviceProgramPageBreaks,
        nextInitialized,
      ),
    );
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-end gap-2">
        <Button
          type="button"
          variant="outline"
          disabled={offer.immutable || reloadSettingsState.isLoading}
          onClick={() => reloadSettings({ offerId: offer.id })}
        >
          {t("Aus Basisdaten synchronisieren")}
        </Button>
        <TooltipProvider>
          {!isEditMode ? (
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  type="button"
                  variant="outline"
                  size="icon"
                  disabled={isLocked}
                  onClick={() => setIsEditMode(true)}
                  aria-label={t("Bearbeiten")}
                >
                  <Pencil className="h-4 w-4" />
                </Button>
              </TooltipTrigger>
              <TooltipContent>
                <p>{t("Bearbeitungsmodus aktivieren")}</p>
              </TooltipContent>
            </Tooltip>
          ) : (
            <>
              <Button
                type="button"
                variant="outline"
                disabled={isLocked}
                onClick={() => {
                  resetLocalFromSnapshot();
                  setIsEditMode(false);
                }}
              >
                {t("Abbrechen")}
              </Button>
              <Button type="button" disabled={isLocked} onClick={triggerSave}>
                {t("Speichern")}
              </Button>
            </>
          )}
        </TooltipProvider>
      </div>

      <ServiceProgramEntryEditor
        entries={entries}
        groups={groups}
        pageBreaks={pageBreaks}
        disabled={isLocked || !isEditMode}
        showEditControls={isEditMode}
        onEntriesChange={setEntries}
        onGroupsChange={setGroups}
        onPageBreaksChange={setPageBreaks}
      />

      {putSettingsState.error ? (
        <RTKQueryErrorAlert error={putSettingsState.error} />
      ) : null}
      {reloadSettingsState.error ? (
        <RTKQueryErrorAlert error={reloadSettingsState.error} />
      ) : null}
    </div>
  );
}
